import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  TextField,
  Button,
  Container,
  Typography,
  Box,
  CircularProgress,
  InputAdornment,
  Paper,
  Link,
  Alert,
} from "@mui/material";
import { Sms, ArrowBack, Phone } from "@mui/icons-material";
import {
  verifyForgotPasswordSms,
  sendForgotPasswordSms,
} from "../services/api";
import logo from "../assets/logos/logo.png";
import loginIcon from "../assets/icons/loginIcon.png";
import "./Login.css";

const ForgotPasswordVerify = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    // Telefon numarasını state'den al
    if (location.state?.phone) {
      setPhone(location.state.phone);
    } else {
      // Eğer state'de telefon numarası yoksa forgot-password sayfasına yönlendir
      navigate("/forgot-password");
    }
  }, [location.state, navigate]);

  const validateCode = (value) => {
    if (!value) {
      setCodeError("Doğrulama kodu alanı zorunludur.");
      return false;
    }
    if (value.length !== 6 || !/^\d+$/.test(value)) {
      setCodeError("Doğrulama kodu 6 haneli olmalıdır.");
      return false;
    }
    setCodeError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    const isCodeValid = validateCode(code);
    if (!isCodeValid) return;

    setLoading(true);
    try {
      const data = {
        TELEFON: phone,
        KOD: code,
      };

      await verifyForgotPasswordSms(data);
      setSuccess(
        "Kod doğrulandı! Yeni şifre belirleme sayfasına yönlendiriliyorsunuz..."
      );

      setTimeout(() => {
        navigate("/forgot-password-reset", { state: { phone } });
      }, 1500);
    } catch (error) {
      console.error("Kod doğrulama hatası:", error);
      setError(
        error.response?.data?.message ||
          "Kod doğrulanırken bir hata oluştu. Lütfen tekrar deneyin."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError("");
    setSuccess("");
    setResendLoading(true);

    try {
      const data = {
        TELEFON: phone,
      };

      await sendForgotPasswordSms(data);
      setSuccess("Yeni kod gönderildi!");
    } catch (error) {
      console.error("Kod yeniden gönderme hatası:", error);
      setError(
        error.response?.data?.message ||
          "Kod gönderilirken bir hata oluştu. Lütfen tekrar deneyin."
      );
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToForgotPassword = () => {
    navigate("/forgot-password");
  };

  return (
    <div className="login-page-container">
      <div className="left-panel">
        <Container
          component={Paper}
          elevation={6}
          className="login-card"
          maxWidth={false}
        >
          <Box className="login-header">
            <img src={logo} alt="eKumes Logo" className="login-logo" />
          </Box>

          <Typography variant="h4" className="login-title" gutterBottom>
            Kod Doğrulama
          </Typography>

          <Typography
            variant="body2"
            sx={{ mb: 3, color: "#666", textAlign: "center" }}
          >
            <strong>{phone}</strong> numarasına gönderilen 6 haneli kodu girin.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} className="login-form">
            <Typography className="input-label">Telefon Numarası</Typography>
            <TextField
              fullWidth
              value={phone}
              disabled
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone className="input-icon phone-icon" />
                  </InputAdornment>
                ),
              }}
              className="login-input"
              sx={{ mb: 2 }}
            />

            <Typography className="input-label">Doğrulama Kodu</Typography>
            <TextField
              fullWidth
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              onBlur={(e) => validateCode(e.target.value)}
              error={!!codeError}
              helperText={codeError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Sms className="input-icon phone-icon" />
                  </InputAdornment>
                ),
              }}
              inputProps={{ maxLength: 6 }}
              type="tel"
              className="login-input"
            />

            <Button
              variant="contained"
              type="submit"
              disabled={loading}
              className="login-button"
              fullWidth
              sx={{ mt: 2 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Kodu Doğrula"
              )}
            </Button>

            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                Kod gelmedi mi?
              </Typography>
              <Button
                variant="text"
                onClick={handleResendCode}
                disabled={resendLoading}
                sx={{ color: "#1976d2", textTransform: "none" }}
              >
                {resendLoading ? (
                  <CircularProgress size={16} />
                ) : (
                  "Kodu Yeniden Gönder"
                )}
              </Button>
            </Box>

            <Box
              className="forgot-password-container"
              sx={{ mt: 2, textAlign: "center" }}
            >
              <Link
                component="button"
                variant="body2"
                onClick={handleBackToForgotPassword}
                className="forgot-password-link"
                sx={{
                  color: "#1976d2",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                <ArrowBack sx={{ fontSize: 16, mr: 0.5 }} />
                Geri dön
              </Link>
            </Box>
          </Box>
        </Container>
      </div>

      <div className="right-panel">
        <div className="image-frame">
          <img
            src={loginIcon}
            alt="Login Visual"
            className="side-image"
            draggable="false"
          />
          <div className="welcome-overlay">
            <Typography variant="h3" className="welcome-text">
              Kod Doğrulama
            </Typography>
            <Typography variant="subtitle1" className="welcome-subtext">
              SMS ile gelen kodu girin
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordVerify;
